import { Chip, Tooltip } from '@nextui-org/react';
import { Link } from 'react-router-dom';
import DashboardSection from './DashboardSection';

// Bloque «Agencias» del Dashboard: las suscripciones de los planes de agencia
// (Rookie, Silver, Gold, Titanium) de Stripe, contadas aparte de los
// creadores. Los datos los calcula el Worker cada hora con el resumen
// (api/backofficeAgencyBilling.js) y llegan en `data.agencies`.

const TIER_LABELS = { rookie: 'Rookie', silver: 'Silver', gold: 'Gold', titanium: 'Titanium' };
const TIER_COLORS = { rookie: '#9ca3af', silver: '#3b82f6', gold: '#eab308', titanium: '#a855f7' };
const STATE_META = {
  active: { label: 'Pagando', color: 'success' },
  canceling: { label: 'Cancela al final', color: 'warning' },
  pastDue: { label: 'En impago', color: 'danger' },
  pending: { label: 'Primer cobro pendiente', color: 'primary' },
  incomplete: { label: 'Pago sin completar', color: 'default' },
  ended: { label: 'Terminada', color: 'default' }
};
const STATE_ORDER = { active: 0, pastDue: 1, canceling: 2, pending: 3, incomplete: 4, ended: 5 };
const CURRENCY_SYMBOLS = { usd: '$', eur: '€' };

function n(value) {
  return Number(value || 0);
}

function fmt(value) {
  return n(value).toLocaleString('es-ES');
}

export function formatMoney(cents, currency, decimals = 0) {
  const amount = Number(cents || 0) / 100;
  const symbol = CURRENCY_SYMBOLS[currency] || `${String(currency || '').toUpperCase()} `;
  return `${symbol}${amount.toLocaleString('es-ES', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
}

export function formatMoneyMap(map, decimals = 0) {
  const parts = Object.entries(map || {})
    .filter(([, cents]) => Number(cents) > 0)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([currency, cents]) => formatMoney(cents, currency, decimals));
  return parts.length ? parts.join(' · ') : '0';
}

function formatDate(ms) {
  const d = new Date(Number(ms) || 0);
  return d.getTime() > 0 ? d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
}

function formatAge(iso) {
  const ms = new Date(iso || 0).getTime();
  if (!ms) return '';
  const minutes = Math.max(0, Math.round((Date.now() - ms) / 60000));
  if (minutes < 1) return 'hace un momento';
  if (minutes < 60) return `hace ${minutes} min`;
  return `hace ${Math.round(minutes / 60)} h`;
}

function InfoTip({ text }) {
  return (
    <Tooltip
      placement="bottom"
      delay={150}
      closeDelay={0}
      content={<div className="max-w-xs px-1 py-1 text-xs leading-5 text-default-600">{text}</div>}
      classNames={{ content: 'border border-default-200 bg-content1 shadow-xl' }}
    >
      <span
        className="inline-flex h-4 w-4 shrink-0 cursor-help items-center justify-center rounded-full border border-default-300 text-[10px] font-semibold leading-none text-default-500 transition hover:border-default-500 hover:text-default-700"
        aria-label="Mas informacion"
      >
        i
      </span>
    </Tooltip>
  );
}

function Stat({ title, info, value, detail, children }) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5 px-5 first:pl-0 last:pr-0">
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-default-500">
        <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: '#a855f7' }} />
        <span className="truncate">{title}</span>
        {info && <InfoTip text={info} />}
      </div>
      <div className="text-2xl font-semibold leading-none">{value}</div>
      {detail && <div className="text-xs text-default-500">{detail}</div>}
      {children}
    </div>
  );
}

export function TierChip({ tier }) {
  if (!tier) return <span className="text-default-400">-</span>;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium">
      <span className="h-2 w-2 rounded-full" style={{ background: TIER_COLORS[tier] || '#9ca3af' }} />
      {TIER_LABELS[tier] || tier}
    </span>
  );
}

export function AgencyStateChip({ state }) {
  const meta = STATE_META[state] || { label: state || '-', color: 'default' };
  return <Chip size="sm" variant="flat" color={meta.color}>{meta.label}</Chip>;
}

// Cambio frente a la foto con la que compara el Dashboard. Las fotos de antes
// de nov-2026 no tienen las agencias: entonces «sin historico».
function AgencyDelta({ value, baseline, label }) {
  if (!baseline || !Object.prototype.hasOwnProperty.call(baseline, 'agenciesActivePaying')) {
    return <span className="text-xs text-default-400">sin historico</span>;
  }
  const delta = value - (n(baseline.agenciesActivePaying) + n(baseline.agenciesPastDue));
  const tone = delta === 0 ? 'text-default-500' : delta > 0 ? 'text-success-600' : 'text-danger-600';
  const arrow = delta > 0 ? '▲' : delta < 0 ? '▼' : '=';
  return (
    <span className={`text-xs font-medium ${tone}`}>
      {arrow} {delta > 0 ? `+${fmt(delta)}` : fmt(delta)}
      {label ? <span className="font-normal text-default-400"> {label}</span> : null}
    </span>
  );
}

const PANEL_SUBTITLE = 'Planes Rookie, Silver, Gold y Titanium. Contador aparte: no entra en los numeros de creadores.';
const PANEL_INFO = 'Suscripciones de los planes de agencia (Rookie, Silver, Gold, Titanium) en Stripe, contadas por suscripcion (una por agencia) y aparte de los creadores. Lo cobrado son facturas pagadas, con impuestos y sin restar reembolsos; la cuota mensual va sin impuestos.';

function AgencyPanel({ right = null, children }) {
  return (
    <DashboardSection title="Agencias" subtitle={PANEL_SUBTITLE} accent="#a855f7" info={<InfoTip text={PANEL_INFO} />} right={right}>
      {children}
    </DashboardSection>
  );
}

export default function AgencyBillingSection({ agencies, canSeeIds = false, baseline = null, vsLabel = '' }) {
  if (!agencies) {
    return (
      <AgencyPanel>
        <div className="text-xs text-default-400">Sin datos todavia. Pulsa Refrescar para leer sus suscripciones en Stripe.</div>
      </AgencyPanel>
    );
  }
  if (!agencies.ok) {
    return (
      <AgencyPanel>
        <div className="text-xs text-warning-600">No se pudieron leer sus suscripciones en Stripe. Vuelve a probar con Refrescar.</div>
      </AgencyPanel>
    );
  }

  const stock = agencies.stock || {};
  const byTier = agencies.byTier || {};
  const live = n(stock.active) + n(stock.pastDue);
  const tierBreakdown = Object.keys(TIER_LABELS)
    .map((tier) => [tier, n(byTier[tier]?.active) + n(byTier[tier]?.pastDue)])
    .filter(([, count]) => count > 0);
  const rows = [...(agencies.subscriptions || [])].sort((a, b) =>
    (STATE_ORDER[a.state] ?? 9) - (STATE_ORDER[b.state] ?? 9) || n(b.createdAt) - n(a.createdAt)
  );
  const unlinked = rows.filter((row) => !row.agencyId && row.state !== 'ended').length;

  return (
    <AgencyPanel
      right={(
        <span className={`text-xs ${agencies.stale ? 'text-warning-600' : 'text-default-400'}`}>
          {agencies.stale
            ? `Stripe no respondio: datos de ${formatAge(agencies.computedAt)}`
            : `Stripe leido ${formatAge(agencies.computedAt)}`}
        </span>
      )}
    >

      <div className="grid grid-cols-2 gap-y-6 md:grid-cols-3 md:divide-x md:divide-default-200 xl:grid-cols-6">
        <Stat
          title="Suscripciones de agencias"
          value={fmt(live)}
          info="Contador aparte de los creadores: agencias con su suscripcion cobrada y que renueva, mas las que estan en impago (una suscripcion por agencia). No entran en Suscriptores activos ni en Suscripciones activas."
          detail={stock.pastDue ? `activas · ${fmt(stock.pastDue)} en impago` : 'activas'}
        >
          <AgencyDelta value={live} baseline={baseline} label={vsLabel} />
          {tierBreakdown.length > 0 && (
            <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-default-500">
              {tierBreakdown.map(([tier, count]) => (
                <span key={tier}><span className="font-medium text-default-700">{fmt(count)}</span> {TIER_LABELS[tier]}</span>
              ))}
            </div>
          )}
        </Stat>
        <Stat
          title="Cuota mensual"
          value={formatMoneyMap(agencies.mrr)}
          info="Lo que pagan cada mes las agencias activas y en impago, sin impuestos."
          detail="al mes, sin impuestos"
        />
        <Stat
          title="Primer cobro pendiente"
          value={fmt(stock.pending)}
          info="Han contratado pero todavia no se les ha cobrado nada (el primer cobro es mas adelante)."
          detail={n(stock.pending) > 0 ? `${formatMoneyMap(agencies.pendingMonthly)}/mes cuando empiecen` : null}
        />
        <Stat
          title="Cancelan / terminadas"
          value={`${fmt(stock.canceling)} / ${fmt(stock.ended)}`}
          info="Con la baja programada (siguen hasta el fin del periodo pagado) y suscripciones ya terminadas."
        />
        <Stat
          title="Cobrado este mes"
          value={formatMoneyMap(agencies.revenue?.month)}
          info="Facturas de agencias pagadas este mes (dia de Madrid), con impuestos."
          detail={`sin impuestos ${formatMoneyMap(agencies.revenueNet?.month)} · 30 dias ${formatMoneyMap(agencies.revenue?.d30)}`}
        />
        <Stat
          title="Cobrado en total"
          value={formatMoneyMap(agencies.revenue?.total)}
          info="Todo lo cobrado a agencias desde la primera, con impuestos."
          detail={`${fmt(agencies.invoices)} cobros · sin impuestos ${formatMoneyMap(agencies.revenueNet?.total)}`}
        />
      </div>

      {rows.length === 0 ? (
        <div className="text-xs text-default-400">Ninguna agencia ha contratado un plan todavia.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px] text-sm">
            <thead>
              <tr className="border-b border-default-300 text-left text-[11px] uppercase tracking-wider text-default-500">
                <th className="py-2 pr-3 font-medium">Agencia</th>
                <th className="py-2 pr-3 font-medium">Plan</th>
                <th className="py-2 pr-3 font-medium">Estado</th>
                <th className="py-2 pr-3 text-right font-medium">Cuota</th>
                <th className="py-2 pr-3 text-right font-medium">Cobrado</th>
                <th className="py-2 pr-3 font-medium">Ultimo cobro</th>
                <th className="py-2 pr-3 font-medium">Proximo cobro / fin</th>
                <th className="py-2 font-medium">Alta</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-b border-default-200/70 odd:bg-default-100/40">
                  <td className="py-2 pr-3">
                    <div className="flex flex-col gap-0.5">
                      {row.agencyId ? (
                        <Link to={`/agencias/${encodeURIComponent(row.agencyId)}`} className="font-medium text-primary hover:underline">
                          {row.name || row.agencyId}
                        </Link>
                      ) : (
                        <span className="font-medium">{row.name || 'Sin nombre'}</span>
                      )}
                      <span className="text-[11px] text-default-400">
                        {row.agencyId
                          ? row.match === 'name'
                            ? 'Enlazada por el nombre'
                            : `${fmt(row.ownerCount)} ${row.ownerCount === 1 ? 'dueño' : 'dueños'} en la app`
                          : 'Sin agencia en la app'}
                        {canSeeIds && (
                          <>
                            {' · '}
                            <a
                              href={`https://dashboard.stripe.com/subscriptions/${encodeURIComponent(row.id)}`}
                              target="_blank"
                              rel="noreferrer"
                              className="hover:underline"
                            >
                              Stripe
                            </a>
                          </>
                        )}
                      </span>
                    </div>
                  </td>
                  <td className="py-2 pr-3"><TierChip tier={row.tier} /></td>
                  <td className="py-2 pr-3"><AgencyStateChip state={row.state} /></td>
                  <td className="py-2 pr-3 text-right tabular-nums">{row.monthly ? `${formatMoney(row.monthly, row.currency)}/mes` : '-'}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">
                    {row.paidCount ? (
                      <Tooltip content={<span className="text-xs">Sin impuestos: {formatMoneyMap(row.paidNet, 2)}</span>}>
                        <span>{formatMoneyMap(row.paidTotal, 2)}</span>
                      </Tooltip>
                    ) : '-'}
                  </td>
                  <td className="py-2 pr-3 text-default-600">{row.lastPaidAt ? formatDate(row.lastPaidAt) : '-'}</td>
                  <td className="py-2 pr-3 text-default-600">
                    {row.state === 'ended'
                      ? formatDate(row.endedAt)
                      : row.state === 'pending'
                        ? formatDate(row.firstChargeAt || row.currentPeriodEnd)
                        : formatDate(row.currentPeriodEnd)}
                  </td>
                  <td className="py-2 text-default-600">{formatDate(row.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {unlinked > 0 && (
            <div className="mt-2 text-xs text-warning-600">
              {unlinked === 1 ? 'Una suscripcion no se ha podido enlazar' : `${fmt(unlinked)} suscripciones no se han podido enlazar`} con una agencia de la app: el correo con el que pagaron no es el del dueño ni el de contacto de ninguna agencia.
            </div>
          )}
        </div>
      )}
    </AgencyPanel>
  );
}
